'use client';
import {type ColorName} from '#/cms/schemas/theme-colors.schema';
import {getIconColor, getTextColor} from '#/utils/get-color';
import {type PepIconName} from '@calvium/pep-icons';
import classNames from 'classnames';
import {type ComponentProps, useCallback} from 'react';
import {useCurrentThemeVariant} from '../store/themeColorsStore';
import {PepIcon} from './PepIcon';
import {SmallText} from './Text';

type Props = ComponentProps<'button'> & {
  active: boolean;
  className?: string;
  clearable?: boolean;
  label: string;
  onClick: () => Promise<void> | void;
  pepIcon?: PepIconName;
  iconHoverColor?: ColorName;
  iconSize?: string;
  variant?: 'primary' | 'secondary';
};

export const IconButton = (props: Props) => {
  const {
    active,
    className,
    clearable,
    iconHoverColor,
    label,
    onClick,
    pepIcon,
    iconSize,
    disabled,
    variant = 'primary',
    ...otherProps
  } = props;
  const theme = useCurrentThemeVariant();
  const handleClick = useCallback(() => onClick(), [onClick]);

  const isPrimary = variant === 'primary';
  const borderColor = isPrimary ? 'primary' : 'midground2';
  const bgColor = isPrimary ? 'primary' : 'background';
  return (
    <button
      className={classNames(
        'group',
        'flex',
        'h-8',
        'shrink-0',
        'flex-row',
        'items-center',
        'gap-x-2',
        'rounded-full',
        'border',
        'px-2 py-1',
        !active && !disabled && `hover:border-secondary`,
        'border-midground3',
        active && !disabled && `border-${borderColor}`,
        active && !disabled && `bg-${bgColor}`,
        active && !disabled && `hover:border-secondary`,
        active && !disabled && `hover:bg-secondary`,
        className
      )}
      onClick={handleClick}
      disabled={disabled}
      {...otherProps}
    >
      {!!pepIcon && (
        <PepIcon
          iconName={pepIcon}
          strokeColor={isPrimary ? getIconColor(theme, active, !disabled) : 'primary'}
          strokeColorGroupHover={
            isPrimary
              ? iconHoverColor && !active && !disabled
                ? iconHoverColor
                : undefined
              : theme === 'light'
                ? 'background'
                : 'foreground'
          }
          size={iconSize}
          className={'ms-1'}
          aria-hidden
        />
      )}
      <SmallText
        className={classNames('mx-1 grow', !isPrimary && 'group-hover:text-light')}
        color={isPrimary ? getTextColor(theme, active, !disabled) : theme === 'light' ? 'dark' : 'light'}
      >
        {label}
      </SmallText>
      {!!clearable && <PepIcon iconName="Remove" strokeColor={getIconColor(theme, active, !disabled)} />}
    </button>
  );
};
